import { create } from "zustand";
import { persist } from "zustand/middleware";
import { Interaction } from "@interfaces/Interactions";
import { RequestDetail } from "@interfaces/Request";
import { useEffect, useMemo } from "react";

interface InteractionsState {
  interactions: Interaction[];
  hasTrackedEvent: boolean;
  deleteDrug: (drug: string, requestDetails: RequestDetail[]) => void;
  setInteractions: (interactions: Interaction[]) => void;
  setHasTrackedEvent: (hasTracked: boolean) => void;
  clearStore: () => void;
}

const normalizeInteraction = (interaction: Interaction) => {
  const drug1 = interaction.drug_1_names.es.toLowerCase().trim();
  const drug2 = interaction.drug_2_names.es.toLowerCase().trim();
  return drug1 < drug2 ? `${drug1}-${drug2}` : `${drug2}-${drug1}`;
};

export const createInteractionsStore = (requestId: string) => {
  return create<InteractionsState>()(
    persist(
      (set) => ({
        interactions: [],
        hasTrackedEvent: false,
        setInteractions: (allInteractions: Interaction[]) =>
          set((state) => {
            if (state.interactions.length === 0) {
              return { interactions: allInteractions };
            }
            const existingCombinations = new Set(
              state.interactions.map(normalizeInteraction),
            );
            const newInteractions = allInteractions.filter(
              (i) => !existingCombinations.has(normalizeInteraction(i)),
            );
            return {
              interactions: [...state.interactions, ...newInteractions],
            };
          }),
        setHasTrackedEvent: (hasTracked: boolean) =>
          set({ hasTrackedEvent: hasTracked }),
        clearStore: () => {
          set({ interactions: [], hasTrackedEvent: false });
          localStorage.removeItem(`interactions-store-${requestId}`);
        },
        deleteDrug: (drug: string, requestDetails: RequestDetail[]) => {
          set((state) => {
            const validDrugs = (requestDetails ?? [])
              .map((detail) => detail.activePrinciple)
              .filter(
                (v): v is string =>
                  typeof v === "string" && v.trim().length > 0,
              )
              .map((d) => d.trim().toLowerCase());

            const targetDrug = drug.trim().toLowerCase();
            const drugIndex = validDrugs.indexOf(targetDrug);
            if (drugIndex === -1) return state;

            const remainingDrugs = validDrugs.filter((_, i) => i !== drugIndex);

            const newInteractions = state.interactions.filter((interaction) => {
              const drug1 = interaction.drug_1_names?.es?.trim().toLowerCase();
              const drug2 = interaction.drug_2_names?.es?.trim().toLowerCase();
              return (
                drug1 &&
                drug2 &&
                remainingDrugs.includes(drug1) &&
                remainingDrugs.includes(drug2)
              );
            });

            return { interactions: newInteractions };
          });
        },
      }),
      {
        name: `interactions-store-${requestId}`,
        partialize: (state) => ({
          interactions: state.interactions,
          hasTrackedEvent: state.hasTrackedEvent,
        }),
      },
    ),
  );
};

export const clearAllInteractionsStores = () => {
  Object.keys(localStorage)
    .filter((key) => key.startsWith("interactions-store"))
    .forEach((key) => localStorage.removeItem(key));
};

export const useClearAllInteractionsStores = () => {
  useEffect(() => {
    clearAllInteractionsStores();
  }, []);
};

const interactionsStoreRegistry = new Map<
  string,
  ReturnType<typeof createInteractionsStore>
>();

export const getInteractionsStore = (requestId: string) => {
  let store = interactionsStoreRegistry.get(requestId);
  if (!store) {
    store = createInteractionsStore(requestId);
    interactionsStoreRegistry.set(requestId, store);
  }
  return store;
};

export const useInteractionsStore = (requestId: string) => {
  const store = useMemo(() => getInteractionsStore(requestId), [requestId]);
  return store();
};
